import React, { useState, useEffect } from "react";
import { getUserRole } from "../utils/Auth";
import { useNavigate } from "react-router-dom";
import { NavLink } from "react-router-dom";
import { Switch, FormControlLabel } from "@mui/material"; // ✅ MUI Switch importálása
import { useTheme } from "../context/ThemeContext"; // 🔥 ThemeContext használata
import API_BASE_URL from "../config";

const Sidebar = ({
  setView,
  view,
  fetchCustomers,
  fetchUsers,
  fetchMyCustomers,
  fetchOrders,
  fetchAllOrders,
}) => {
  const role = getUserRole();
  const navigate = useNavigate();
  const { darkMode, toggleTheme } = useTheme(); // 🔥 A ThemeContext-ből vesszük a témát
  const [loading, setLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false); // 🔥 Mobil menü állapot
  const [reminderCount, setReminderCount] = useState(0);

  useEffect(() => {
    const savedView = localStorage.getItem("view");
    if (savedView) {
      setView(savedView);
      loadData(savedView);
    } else {
      setView("dashboard");
    }
  }, [setView]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const fetchReminders = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/tasks/reminders?hoursBefore=2`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (res.ok && data.success && Array.isArray(data.reminders)) {
          setReminderCount(data.reminders.length);
        }
      } catch (err) {
        console.error("⚠️ Reminder fetch error:", err);
      }
    };

    fetchReminders();

    const interval = setInterval(fetchReminders, 120000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async (view) => {
    setLoading(true);
    switch (view) {
      case "customers":
        await fetchCustomers();
        break;
      case "myCustomers":
        await fetchMyCustomers();
        break;
      case "users":
        if (role === "Admin") {
          await fetchUsers();
        }
        break;
      case "orders":
        await fetchOrders();
        break;
      case "allOrders":
        await fetchAllOrders();
        break;
      default:
        break;
    }
    setLoading(false);
  };

  const handleViewChange = (newView, route) => {
    localStorage.setItem("view", newView);
    setView(newView);
    navigate(route);
    loadData(newView);
  };

  return (
    <>
      {/* 🔥 Hamburger menü mobil nézethez */}
      <button
        className="fixed top-4 left-4 z-50 p-2 rounded-md bg-gray-800 text-white md:hidden"
        onClick={() => setMenuOpen(!menuOpen)}
      >
        ☰
      </button>

      <div
        className={`fixed top-0 left-0 h-full w-64 p-6 transition-all duration-300 z-40 
        ${darkMode ? "bg-red-900 text-white" : "bg-gray-300 text-black"} 
        ${menuOpen ? "translate-x-0" : "-translate-x-64"} md:translate-x-0`}
      >
        <h2 className="text-xl font-bold mb-6">
          Panou {role === "Admin" ? "Admin" : "Agent"}
        </h2>

        <NavLink
          to="/dashboard"
          end
          className={({ isActive }) =>
            `w-full p-2 text-left rounded block ${isActive
              ? darkMode
                ? "bg-red-700"
                : "bg-gray-400"
              : darkMode
                ? "hover:bg-red-700"
                : "hover:bg-gray-400"
            }`
          }
        >
          Dashboard
        </NavLink>

        {role === "Admin" && (
          <NavLink
            to="/dashboard/users"
            className={({ isActive }) =>
              `w-full p-2 text-left rounded block ${isActive
                ? darkMode
                  ? "bg-red-700"
                  : "bg-gray-400"
                : darkMode
                  ? "hover:bg-red-700"
                  : "hover:bg-gray-400"
              }`
            }
          >
            Utilizatori
          </NavLink>
        )}

        <NavLink
          to="/dashboard/myCustomers"
          className={({ isActive }) =>
            `w-full p-2 text-left rounded block ${isActive
              ? darkMode
                ? "bg-red-700"
                : "bg-gray-400"
              : darkMode
                ? "hover:bg-red-700"
                : "hover:bg-gray-400"
            }`
          }
        >
          Clienții mei
        </NavLink>

        {role === "Admin" && (
          <NavLink
            to="/dashboard/customers"
            className={({ isActive }) =>
              `w-full p-2 text-left rounded block ${isActive
                ? darkMode
                  ? "bg-red-700"
                  : "bg-gray-400"
                : darkMode
                  ? "hover:bg-red-700"
                  : "hover:bg-gray-400"
              }`
            }
          >
            Clienți
          </NavLink>
        )}

        <NavLink
          to="/dashboard/tasks"
          className={({ isActive }) =>
            `w-full p-2 text-left rounded block ${isActive
              ? darkMode
                ? "bg-red-700"
                : "bg-gray-400"
              : darkMode
                ? "hover:bg-red-700"
                : "hover:bg-gray-400"
            }`
          }
        >
          Task-uri
        </NavLink>

        <NavLink
          to="/dashboard/reminders"
          className={({ isActive }) =>
            `relative w-full p-2 text-left rounded block ${isActive
              ? darkMode
                ? "bg-red-700"
                : "bg-gray-400"
              : darkMode
                ? "hover:bg-red-700"
                : "hover:bg-gray-400"
            }`
          }
        >
          Reminderi
          {reminderCount > 0 && (
            <span
              className={`absolute right-3 top-2 text-xs font-bold rounded-full px-2 py-0.5 ${darkMode ? "bg-yellow-400 text-black" : "bg-red-600 text-white"
                }`}
            >
              {reminderCount}
            </span>
          )}
        </NavLink>

        <NavLink
          to="/dashboard/orders"
          className={({ isActive }) =>
            `w-full p-2 text-left rounded block ${isActive
              ? darkMode
                ? "bg-red-700"
                : "bg-gray-400"
              : darkMode
                ? "hover:bg-red-700"
                : "hover:bg-gray-400"
            }`
          }
        >
          Comenzile mele
        </NavLink>

        {role === "Admin" && (
          <NavLink
            to="/dashboard/allOrders"
            className={({ isActive }) =>
              `w-full p-2 text-left rounded block ${isActive
                ? darkMode
                  ? "bg-red-700"
                  : "bg-gray-400"
                : darkMode
                  ? "hover:bg-red-700"
                  : "hover:bg-gray-400"
              }`
            }
          >
            Toate comenzile
          </NavLink>
        )}

        <NavLink
          to="/dashboard/offers"
          className={({ isActive }) =>
            `w-full p-2 text-left rounded block ${isActive
              ? darkMode
                ? "bg-red-700"
                : "bg-gray-400"
              : darkMode
                ? "hover:bg-red-700"
                : "hover:bg-gray-400"
            }`
          }
        >
          Ofertele mele
        </NavLink>

        {role === "Admin" && (
          <NavLink
            to="/dashboard/allOffers"
            className={({ isActive }) =>
              `w-full p-2 text-left rounded block ${isActive
                ? darkMode
                  ? "bg-red-700"
                  : "bg-gray-400"
                : darkMode
                  ? "hover:bg-red-700"
                  : "hover:bg-gray-400"
              }`
            }
          >
            Toate ofertele
          </NavLink>
        )}

        <NavLink
          to="/stoc"
          className={({ isActive }) =>
            `w-full p-2 text-left rounded block ${isActive
              ? darkMode
                ? "bg-red-700"
                : "bg-gray-400"
              : darkMode
                ? "hover:bg-red-700"
                : "hover:bg-gray-400"
            }`
          }
        >
          Stoc
        </NavLink>

        <NavLink
          to="/dashboard/customer-stats"
          className={({ isActive }) =>
            `w-full p-2 text-left rounded block ${isActive
              ? darkMode
                ? "bg-red-700"
                : "bg-gray-400"
              : darkMode
                ? "hover:bg-red-700"
                : "hover:bg-gray-400"
            }`
          }
        >
          Statisticile clienților
        </NavLink>

        {role === "Admin" && (
          <NavLink
            to="/dashboard/sales-stats"
            className={({ isActive }) =>
              `w-full p-2 text-left rounded block ${isActive
                ? darkMode ? "bg-red-700" : "bg-gray-400"
                : darkMode ? "hover:bg-red-700" : "hover:bg-gray-400"
              }`
            }
          >
            Statistici vânzări
          </NavLink>
        )}

        {role === "Admin" && (
          <NavLink
            to="/dashboard/top-products"
            className={({ isActive }) =>
              `w-full p-2 text-left rounded block ${isActive
                ? darkMode ? "bg-red-700" : "bg-gray-400"
                : darkMode ? "hover:bg-red-700" : "hover:bg-gray-400"
              }`
            }
          >
            Top produse
          </NavLink>
        )}

        {role === "Admin" && (
          <NavLink
            to="/dashboard/audit-logs"
            className={({ isActive }) =>
              `w-full p-2 text-left rounded block ${isActive
                ? darkMode ? "bg-red-700" : "bg-gray-400"
                : darkMode ? "hover:bg-red-700" : "hover:bg-gray-400"
              }`
            }
          >
            Istoric acțiuni
          </NavLink>
        )}

        <NavLink
          to="/change-password"
          className={({ isActive }) =>
            `w-full p-2 text-left rounded block ${isActive
              ? darkMode
                ? "bg-red-700"
                : "bg-gray-400"
              : darkMode
                ? "hover:bg-red-700"
                : "hover:bg-gray-400"
            }`
          }
        >
          Schimbare parolă{" "}
        </NavLink>

        {/* 🔥 Dark Mode kapcsoló */}
        <div className="mt-6">
          <FormControlLabel
            control={<Switch checked={darkMode} onChange={toggleTheme} />}
            label={darkMode ? "🌙 Dark mode" : "☀️ Light mode"}
            className="cursor-pointer"
          />
        </div>
      </div>
    </>
  );
};

export default Sidebar;
